import { AccountInfo, AuthError, EventMessage, InteractionRequiredAuthError } from "@azure/msal-browser";

interface BaseIdentityClaims {
    exp: number;
    iat: number;
    iss: string;
    nbf: number;
    nonce: string;
    /** Pairwise per app in Entra External ID - use `oid` as the stable user id. */
    sub: string;
    /** Stable user object id (Entra External ID). */
    oid?: string;
    /** Opaque hint Entra accepts back as login_hint / logout_hint (optional claim, Entra External ID). */
    login_hint?: string;
    /**
     * Issuer of the federated provider the user actually signed in with - present only for federated
     * users (n4iT staff), absent for local Orbital Watch accounts. Entra External ID formats it as
     * `https://login.microsoftonline.com/{workforceTenantId}/v2.0<{externalTenantId}>`.
     */
    idp?: string;
    /** B2C only (user-flow name); not issued by Entra External ID. */
    tfp?: string;
    ver: string;
    aud: string;
    auth_time: number;
    preferred_username:string;
}

export interface IdentityClaims extends BaseIdentityClaims {
    city: string;
    country: string;
    /** Entra External ID email claim. */
    email?: string;
    /** B2C only (array claim); not issued by Entra External ID. */
    emails?: string[],
    name: string;
    jobTitle: string;
    postalCode: string;
    state: string;
    streetAddress: string;
};




export class IdentityHelper {

    /** The value Entra expects in login_hint / logout_hint: the `login_hint` claim, else the account username. Never the display name. */
    static getLoginHint(account: AccountInfo | null | undefined): string | undefined {
        const claims = account?.idTokenClaims as Partial<IdentityClaims> | undefined;
        return claims?.login_hint || account?.username || claims?.preferred_username || undefined;
    }

    /**
     * The same value taken from a stored claims object rather than a live account. The Session Expired
     * reset drops the MSAL account, so this is what is left to pre-fill the address with.
     */
    static getLoginHintFromClaims(claims: Partial<IdentityClaims> | null | undefined): string | undefined {
        if (!claims) return undefined;
        return claims.login_hint || claims.preferred_username || claims.email || undefined;
    }

    /**
     * NOT SENT ANY MORE - kept only because the value is still recorded alongside the login hint.
     *
     * Two findings killed it. It never worked: the custom OIDC provider has no domain associated
     * (the External ID portal exposes no field for one), so Entra ignored both `n4it.pt` and
     * `n4it.onmicrosoft.com` - verified against the live authorize endpoint. And it actively broke
     * sign-in: our login_hint is Entra's opaque form, and combining the two is rejected outright -
     *   AADSTS1002014: domain_hint and opaque login_hint cannot be used together.
     * login_hint alone does the real work (address pre-filled, home realm discovery routes to n4iT).
     */
    static getDomainHint(
        account: AccountInfo | null | undefined,
        federatedIdps: { Issuer: string; DomainHint: string }[] | undefined | null,
        claims?: Partial<IdentityClaims> | null,
    ): string | undefined {
        if (!federatedIdps || federatedIdps.length === 0) return undefined;
        const idp = (account?.idTokenClaims as Partial<IdentityClaims> | undefined)?.idp || claims?.idp;
        if (!idp) return undefined;
        return federatedIdps.find((p) => p.Issuer && idp.indexOf(p.Issuer) >= 0)?.DomainHint;
    }

    /** True when MSAL says the user must interact again (expired IdP session, consent, login_required). */
    static isInteractionRequired(error: unknown): boolean {
        if (!error) return false;
        if (error instanceof InteractionRequiredAuthError) return true;
        const code = (error as { errorCode?: string; name?: string }).errorCode ?? (error as { name?: string }).name;
        return code === "interaction_required" || code === "consent_required" || code === "login_required" || code === "InteractionRequiredAuthError";
    }




    static getUserDisplayNameFromClaims(principal: IdentityClaims) {
        return principal && principal.name;
    }

    static getUserEmailFromClaims(principal: IdentityClaims) {

        if (principal
            && principal.email) {

            return principal.email;
        }

        else if (principal
            && principal.emails
            && Array.isArray(principal.emails)
            && principal.emails.length > 0) {

            return principal.emails[0];
        }

        else if(principal
            && principal.preferred_username) {
                return principal.preferred_username;
            }

        else return null;
    }


    static errorMessageContains(message: EventMessage, text: string) {

        return (message
            && message.error
            && message.error instanceof AuthError
            && message.error.errorMessage
            && message.error.errorMessage.includes(text));

    }
}
